// Tile-code conversions. Canonical representation is the mjai tile string:
// "1m".."9m", "1p".."9p", "1s".."9s", red fives "5mr"/"5pr"/"5sr",
// honors "E" "S" "W" "N" "P" "F" "C" (haku/hatsu/chun).

const HONORS = ["E", "S", "W", "N", "P", "F", "C"] as const;

const MJAI_TILE_RE = /^([1-9][mps]|5[mps]r|[ESWNPFC]|\?)$/;

/** Convert a mjlog tile id (0-135) to a mjai tile string. Red fives are ids 16/52/88. */
export function tileFromMjlogId(id: number): string {
    if (!Number.isInteger(id) || id < 0 || id > 135) {
        throw new Error(`invalid mjlog tile id: ${id}`);
    }
    if (id === 16) return "5mr";
    if (id === 52) return "5pr";
    if (id === 88) return "5sr";
    const base = Math.floor(id / 4);
    if (base < 9) return `${base + 1}m`;
    if (base < 18) return `${base - 8}p`;
    if (base < 27) return `${base - 17}s`;
    return HONORS[base - 27] as string;
}

/** Convert a Tenhou-JSON two-digit tile code (11-47, red 51/52/53) to a mjai tile string. */
export function tileFromTenhouCode(code: number): string {
    if (code === 51) return "5mr";
    if (code === 52) return "5pr";
    if (code === 53) return "5sr";
    const suit = Math.floor(code / 10);
    const rank = code % 10;
    if (suit >= 1 && suit <= 3 && rank >= 1 && rank <= 9) {
        return `${rank}${"mps"[suit - 1]}`;
    }
    if (suit === 4 && rank >= 1 && rank <= 7) {
        return HONORS[rank - 1] as string;
    }
    throw new Error(`invalid tenhou tile code: ${code}`);
}

/**
 * Normalize a mjai tile string: accepts "0m"/"0p"/"0s" red-five aliases and
 * "1z".."7z" honor aliases; validates everything else. "?" (masked) passes through.
 */
export function normalizeMjaiTile(pai: string): string {
    if (pai === "0m") return "5mr";
    if (pai === "0p") return "5pr";
    if (pai === "0s") return "5sr";
    const zMatch = /^([1-7])z$/.exec(pai);
    if (zMatch) return HONORS[Number(zMatch[1]) - 1] as string;
    if (MJAI_TILE_RE.test(pai)) return pai;
    throw new Error(`invalid mjai tile: ${pai}`);
}
